import { useState } from "react"
import { Box, Card, CardContent, CardHeader, Chip, Skeleton, Tab, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, Tabs, Typography } from "@mui/material"
import { useTheme } from "@mui/material/styles"
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer
} from 'recharts'
import { Package, Layers, Grid3X3, DollarSign, TrendingUp, ShoppingCart, FileText } from "lucide-react"
import { formatCurrency } from "@crm-src/lib/utils"

interface ProductItem {
  code: string
  name: string
  quantity: number
  revenue: number
  count: number
  average_price: number
  product?: string
  feature?: string
  version?: string
}

interface CategoryData {
  products: ProductItem[]
  features: ProductItem[]
  versions: ProductItem[]
  unknown: ProductItem[]
  totals: {
    quantity: number
    revenue: number
    count: number
  }
  revenue_distribution?: Array<{
    name: string
    value: number
  }>
}

interface MonthlyTrend {
  month: string
  sold_revenue: number
  sold_quantity: number
  quoted_revenue: number
  quoted_quantity: number
}

interface ProductPerformanceData {
  period: string
  date_range: {
    start_date: string
    end_date: string
  }
  sold: CategoryData
  quoted: CategoryData
  monthly_trends: MonthlyTrend[]
}

interface ProductPerformanceTabProps {
  data: ProductPerformanceData
  isLoading?: boolean
  period: string
}

const COLORS = ['#3b82f6', '#10b981', '#f59e0b', '#ef4444', '#8b5cf6', '#06b6d4', '#f97316', '#84cc16']

export function ProductPerformanceTab({ data, isLoading, period }: ProductPerformanceTabProps) {
  // The two bar series were filled by --color-good / --color-brand CSS vars;
  // bridge to the MUI palette now that the vars are gone (Reference A.7).
  const theme = useTheme()
  const soldBarFill = theme.palette.success.main
  const quotedBarFill = theme.palette.primary.main

  // Two independent tab groups (sold / quoted) each default to "products".
  const [soldTab, setSoldTab] = useState('products')
  const [quotedTab, setQuotedTab] = useState('products')

  if (isLoading) {
    return (
      <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
        <Box sx={{ display: "grid", gridTemplateColumns: "repeat(4, 1fr)", gap: 2 }}>
          {[1, 2, 3, 4].map((i) => (
            <Skeleton key={i} variant="rounded" sx={{ height: 128 }} />
          ))}
        </Box>
        <Skeleton variant="rounded" sx={{ height: 384 }} />
      </Box>
    )
  }

  if (!data || !data.sold) {
    return (
      <Box sx={{ textAlign: "center", py: 6, color: "text.secondary" }}>
        <Box sx={{ display: "flex", justifyContent: "center", mb: 2, opacity: 0.3 }}><Package size={48} /></Box>
        <Typography>No product data available for the selected period</Typography>
      </Box>
    )
  }

  const soldData = data.sold
  const quotedData = data.quoted

  // Prepare chart data for top products only (not features/versions)
  const topSellingItems = soldData.products
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10)
    .map(p => ({ ...p, category: 'Product', icon: '📦' }))

  const topQuotedItems = quotedData.products
    .sort((a, b) => b.revenue - a.revenue)
    .slice(0, 10)
    .map(p => ({ ...p, category: 'Product', icon: '📦' }))

  // Render category performance section with hierarchical display
  const renderCategoryTable = (items: ProductItem[], category: string) => {
    if (!items || items.length === 0) {
      return (
        <Box sx={{ textAlign: "center", py: 4, color: "text.secondary" }}>
          <Typography>No {category.toLowerCase()} data available</Typography>
        </Box>
      )
    }

    // Format the display name based on category type
    const getDisplayName = (item: ProductItem) => {
      if (category === 'Product') {
        // Products: Just show product name
        return item.name
      } else if (category === 'Feature') {
        // Features: Show "Product > Feature"
        return `${item.product || 'Unknown'} > ${item.name}`
      } else if (category === 'Version') {
        // Versions: Show "Product > Feature > v{version_code}"
        const versionCode = item.version || item.code.split('-').pop()
        return `${item.product || 'Unknown'} > ${item.feature || 'Unknown'} > v${versionCode}`
      } else {
        // Unknown: Just show name
        return item.name
      }
    }

    return (
      <TableContainer>
        <Table>
          <TableHead>
            <TableRow>
              <TableCell>Rank</TableCell>
              <TableCell>{category === 'Product' ? 'Product' : category === 'Feature' ? 'Product > Feature' : category === 'Version' ? 'Product > Feature > Version' : 'Name'}</TableCell>
              <TableCell align="right">Qty</TableCell>
              <TableCell align="right">Revenue</TableCell>
              <TableCell align="right">Avg Price</TableCell>
              <TableCell align="right">Orders</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {items.map((item, index) => (
              <TableRow key={item.code}>
                <TableCell>
                  <Chip
                    size="small"
                    label={`#${index + 1}`}
                    {...(index < 3 ? { color: "primary" as const } : { variant: "outlined" as const })}
                  />
                </TableCell>
                <TableCell sx={{ fontWeight: 500 }}>
                  {getDisplayName(item)}
                </TableCell>
                <TableCell align="right">{item.quantity}</TableCell>
                <TableCell align="right" sx={{ fontWeight: 600, color: "success.main" }}>
                  {formatCurrency(item.revenue, { decimals: 0 })}
                </TableCell>
                <TableCell align="right">
                  {formatCurrency(item.average_price, { decimals: 0 })}
                </TableCell>
                <TableCell align="right">{item.count}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
    )
  }

  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: 3 }}>
      {/* Summary Cards */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", md: "repeat(4, 1fr)" }, gap: 2 }}>
        <Card sx={{ borderColor: "success.main", bgcolor: (t) => `${t.palette.success.main}14` }}>
          <CardHeader
            sx={{ pb: 1.5 }}
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "0.875rem", fontWeight: 500, color: "text.secondary" }}>
                <DollarSign size={16} />
                Total Sold Revenue
              </Box>
            }
          />
          <CardContent>
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "success.main" }}>
              {formatCurrency(soldData.totals.revenue, { decimals: 0 })}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.5 }}>
              {soldData.totals.quantity} items • {soldData.totals.count} orders
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ borderColor: "info.main", bgcolor: (t) => `${t.palette.info.main}14` }}>
          <CardHeader
            sx={{ pb: 1.5 }}
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "0.875rem", fontWeight: 500, color: "text.secondary" }}>
                <FileText size={16} />
                Total Quoted Revenue
              </Box>
            }
          />
          <CardContent>
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "info.main" }}>
              {formatCurrency(quotedData.totals.revenue, { decimals: 0 })}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.5 }}>
              {quotedData.totals.quantity} items • {quotedData.totals.count} quotes
            </Typography>
          </CardContent>
        </Card>

        <Card sx={{ borderColor: "#9333ea", bgcolor: "#9333ea14" }}>
          <CardHeader
            sx={{ pb: 1.5 }}
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "0.875rem", fontWeight: 500, color: "text.secondary" }}>
                <TrendingUp size={16} />
                Conversion Rate
              </Box>
            }
          />
          <CardContent>
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "#9333ea" }}>
              {quotedData.totals.revenue > 0
                ? ((soldData.totals.revenue / quotedData.totals.revenue) * 100).toFixed(1)
                : '0'}%
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.5 }}>Quote to sale conversion</Typography>
          </CardContent>
        </Card>

        <Card sx={{ borderColor: "warning.main", bgcolor: (t) => `${t.palette.warning.main}14` }}>
          <CardHeader
            sx={{ pb: 1.5 }}
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1, fontSize: "0.875rem", fontWeight: 500, color: "text.secondary" }}>
                <ShoppingCart size={16} />
                Avg Order Value
              </Box>
            }
          />
          <CardContent>
            <Typography sx={{ fontSize: "1.5rem", fontWeight: 700, color: "warning.main" }}>
              {soldData.totals.count > 0
                ? formatCurrency(soldData.totals.revenue / soldData.totals.count, { decimals: 0 })
                : formatCurrency(0, { decimals: 0 })}
            </Typography>
            <Typography sx={{ fontSize: "0.75rem", color: "text.secondary", mt: 0.5 }}>Per sold order</Typography>
          </CardContent>
        </Card>
      </Box>

      {/* Top Performers Comparison */}
      <Box sx={{ display: "grid", gridTemplateColumns: { xs: "1fr", lg: "1fr 1fr" }, gap: 3 }}>
        {/* Top Sold Items */}
        <Card>
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ display: "flex", color: "success.main" }}><TrendingUp size={20} /></Box>
                Top 10 Sold Items
              </Box>
            }
            subheader="Best performing items by revenue"
          />
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={topSellingItems} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={150}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip
                  formatter={(value: any) => formatCurrency(Number(value), { decimals: 0 })}
                  labelFormatter={(label) => {
                    const item = topSellingItems.find(i => i.name === label)
                    return `${item?.icon} ${label} (${item?.category})`
                  }}
                />
                <Bar dataKey="revenue" fill={soldBarFill} name="Revenue" barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Top Quoted Items */}
        <Card>
          <CardHeader
            title={
              <Box sx={{ display: "flex", alignItems: "center", gap: 1 }}>
                <Box sx={{ display: "flex", color: "info.main" }}><FileText size={20} /></Box>
                Top 10 Quoted Items
              </Box>
            }
            subheader="Most requested items in quotes"
          />
          <CardContent>
            <ResponsiveContainer width="100%" height={250}>
              <BarChart data={topQuotedItems} layout="vertical">
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis type="number" />
                <YAxis
                  dataKey="name"
                  type="category"
                  width={150}
                  tick={{ fontSize: 11 }}
                />
                <Tooltip
                  formatter={(value: any) => formatCurrency(Number(value), { decimals: 0 })}
                  labelFormatter={(label) => {
                    const item = topQuotedItems.find(i => i.name === label)
                    return `${item?.icon} ${label} (${item?.category})`
                  }}
                />
                <Bar dataKey="revenue" fill={quotedBarFill} name="Quoted Value" barSize={20} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </Box>

      {/* Detailed Tables */}
      <Card>
        <CardHeader
          title={<Typography variant="h6">Detailed Performance by Category</Typography>}
          subheader="Sold items breakdown"
        />
        <CardContent>
          <Tabs value={soldTab} onChange={(_e, v) => setSoldTab(v)} variant="fullWidth" sx={{ mb: 3 }}>
            <Tab value="products" iconPosition="start" icon={<Package size={16} />} label={`Products (${soldData.products.length})`} />
            <Tab value="features" iconPosition="start" icon={<Layers size={16} />} label={`Features (${soldData.features.length})`} />
            <Tab value="versions" iconPosition="start" icon={<Grid3X3 size={16} />} label={`Versions (${soldData.versions.length})`} />
            <Tab value="unknown" iconPosition="start" icon={<FileText size={16} />} label={`Unknown (${soldData.unknown.length})`} />
          </Tabs>

          {soldTab === 'products' && renderCategoryTable(soldData.products, 'Product')}
          {soldTab === 'features' && renderCategoryTable(soldData.features, 'Feature')}
          {soldTab === 'versions' && renderCategoryTable(soldData.versions, 'Version')}
          {soldTab === 'unknown' && renderCategoryTable(soldData.unknown, 'Unknown Item')}
        </CardContent>
      </Card>

      {/* Quoted Items Table */}
      <Card>
        <CardHeader
          title={<Typography variant="h6">Quoted Items Analysis</Typography>}
          subheader="Items requested in quotes"
        />
        <CardContent>
          <Tabs value={quotedTab} onChange={(_e, v) => setQuotedTab(v)} variant="fullWidth" sx={{ mb: 3 }}>
            <Tab value="products" iconPosition="start" icon={<Package size={16} />} label={`Products (${quotedData.products.length})`} />
            <Tab value="features" iconPosition="start" icon={<Layers size={16} />} label={`Features (${quotedData.features.length})`} />
            <Tab value="versions" iconPosition="start" icon={<Grid3X3 size={16} />} label={`Versions (${quotedData.versions.length})`} />
            <Tab value="unknown" iconPosition="start" icon={<FileText size={16} />} label={`Unknown (${quotedData.unknown.length})`} />
          </Tabs>

          {quotedTab === 'products' && renderCategoryTable(quotedData.products, 'Product')}
          {quotedTab === 'features' && renderCategoryTable(quotedData.features, 'Feature')}
          {quotedTab === 'versions' && renderCategoryTable(quotedData.versions, 'Version')}
          {quotedTab === 'unknown' && renderCategoryTable(quotedData.unknown, 'Unknown Item')}
        </CardContent>
      </Card>
    </Box>
  )
}
